import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { ArrowLeft, Trash, Database, Sparkle, Fire, Printer, CalendarPlus, Share, FlowArrow, ArrowsClockwise, Warning as WarningIcon, CheckCircle, MicrophoneStage, Barbell } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getRecipe, deleteRecipe, getDailyValues, addToMealLog, getRecipeCombinations, getAminoScore, getSubstitutions, remixRecipe, NUTRIENT_LABELS, NUTRIENT_UNITS, MACRO_KEYS, VITAMIN_KEYS, MINERAL_KEYS, DIET_TAG_META } from "../lib/api";
import NutrientView from "../components/NutrientView";
import { useNavigate } from "react-router-dom";

const MACRO_COLORS = { protein: "#7A9A7C", carbs: "#D99D41", fat: "#C84C31", fiber: "#4A5559" };
const AXIS_TICK = { fontSize: 10, fill: "hsl(var(--muted-foreground))", fontFamily: "JetBrains Mono" };
const BAR_MARGIN = { left: 10, right: 20 };
const TOOLTIP_CURSOR = { fill: "hsl(var(--muted))" };
const TOOLTIP_STYLE = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 };

function num(v, digits = 1) {
  if (v == null || Number.isNaN(v)) return "0";
  const n = Number(v);
  if (Math.abs(n) >= 100) return n.toFixed(0);
  if (Math.abs(n) >= 10) return n.toFixed(1);
  return n.toFixed(digits);
}

function DataCell({ label, value, unit, dv, testId }) {
  const pct = dv ? Math.min(999, Math.round((value / dv) * 100)) : null;
  return (
    <div className="border border-border p-4 bg-card" data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="mt-2 flex items-baseline gap-1.5">
        <span className="font-mono text-2xl md:text-3xl font-medium tracking-tight">{num(value)}</span>
        <span className="font-mono text-xs text-muted-foreground">{unit}</span>
      </div>
      {pct !== null && (
        <div className="mt-3">
          <div className="flex items-baseline justify-between mb-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Daily value</span>
            <span className="font-mono text-xs font-medium">{pct}%</span>
          </div>
          <div className="h-1.5 bg-muted overflow-hidden">
            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${Math.min(100, pct)}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function RecipeResult({ readOnly = false }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [dv, setDv] = useState({});
  const [perServing, setPerServing] = useState(true);
  const [combos, setCombos] = useState([]);
  const [amino, setAmino] = useState(null);
  const [subs, setSubs] = useState(null);
  const [subsLoading, setSubsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, d] = await Promise.all([getRecipe(id), getDailyValues()]);
        setRecipe(r); setDv(d.daily_values || {});
        try {
          const c = await getRecipeCombinations(id);
          setCombos(c.combinations || []);
        } catch (e) { /* noop */ }
        try {
          const a = await getAminoScore(id);
          if (a.available) setAmino(a);
        } catch (e) { /* noop */ }
      } catch (e) { toast.error("Failed to load recipe"); }
    })();
  }, [id]);

  const values = useMemo(() => perServing ? recipe?.per_serving : recipe?.totals, [perServing, recipe]);

  const macroPie = useMemo(() => {
    if (!values) return [];
    const p = (values.protein || 0) * 4;
    const c = (values.carbs || 0) * 4;
    const f = (values.fat || 0) * 9;
    const total = p + c + f || 1;
    return [
      { name: "Protein", value: p, pct: Math.round(p / total * 100), color: MACRO_COLORS.protein },
      { name: "Carbs", value: c, pct: Math.round(c / total * 100), color: MACRO_COLORS.carbs },
      { name: "Fat", value: f, pct: Math.round(f / total * 100), color: MACRO_COLORS.fat },
    ];
  }, [values]);

  const radarData = useMemo(() => {
    if (!values || !dv) return [];
    return VITAMIN_KEYS.map(k => ({
      nutrient: NUTRIENT_LABELS[k].replace("Vitamin ", "Vit "),
      value: Math.min(200, Math.round((values[k] / (dv[k] || 1)) * 100)),
    }));
  }, [values, dv]);

  const mineralBars = useMemo(() => {
    if (!values || !dv) return [];
    return MINERAL_KEYS.map(k => ({
      name: NUTRIENT_LABELS[k],
      pct: Math.min(200, Math.round((values[k] / (dv[k] || 1)) * 100)),
    }));
  }, [values, dv]);

  const remove = async () => {
    if (!window.confirm("Delete this recipe?")) return;
    await deleteRecipe(id);
    toast.success("Deleted");
    nav("/library");
  };

  const addLog = async () => {
    try {
      const date = new Date().toISOString().slice(0, 10);
      await addToMealLog(date, id, 1);
      toast.success(`Added to today's log`);
    } catch (e) { toast.error("Failed to add to log"); }
  };

  const share = async () => {
    try {
      const url = `${window.location.origin}/share/${id}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard");
    } catch (e) {
      toast.error("Copy failed — long-press the URL bar to copy");
    }
  };

  const loadSubs = async () => {
    setSubsLoading(true);
    try {
      const s = await getSubstitutions(id, 5);
      setSubs(s);
    } catch (e) { toast.error("Failed to load suggestions"); }
    finally { setSubsLoading(false); }
  };

  const applySub = async (sug) => {
    try {
      let newIngs;
      if (sug.kind === "add") {
        newIngs = recipe.ingredients.map(i => ({ name: i.name, quantity: i.quantity, unit: i.unit }));
        newIngs.push({ name: sug.ingredient, quantity: sug.grams, unit: "g" });
      } else {
        // swap
        newIngs = recipe.ingredients.map(i => (
          i.name === sug.replaces
            ? { name: sug.ingredient, quantity: i.quantity, unit: i.unit }
            : { name: i.name, quantity: i.quantity, unit: i.unit }
        ));
      }
      const label = sug.kind === "add" ? `+ ${sug.ingredient}` : `${sug.replaces} → ${sug.ingredient}`;
      const r = await remixRecipe(id, `${recipe.name} · ${label}`, recipe.servings, newIngs, true);
      toast.success("Applied — opening new recipe");
      nav(`/recipe/${r.remixed.id}`);
    } catch (e) { toast.error("Apply failed"); }
  };

  if (!recipe) return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 font-mono text-sm text-muted-foreground">Loading recipe…</div>
  );

  return (
    <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 space-y-6" data-testid="recipe-result">
      {/* header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-border">
        <div>
          <Link to="/library" data-testid="back-to-library" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 mb-3">
            <ArrowLeft size={14} /> Recipe Library
          </Link>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            {recipe.source_type} · {new Date(recipe.created_at).toLocaleString()}
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter mt-2" data-testid="recipe-name">
            {recipe.name}
          </h1>
          <div className="mt-3 font-mono text-sm text-muted-foreground">
            {recipe.ingredients.length} ingredients · {recipe.servings} serving{recipe.servings > 1 ? "s" : ""}
          </div>
          {(recipe.diet_tags || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid="diet-tags">
              {recipe.diet_tags.map(t => {
                const meta = DIET_TAG_META[t] || { label: t, color: "#888" };
                return (
                  <span key={t} data-testid={`tag-${t}`}
                    className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border"
                    style={{ borderColor: meta.color, color: meta.color }}>
                    {meta.label}
                  </span>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="border border-border flex" data-testid="serving-toggle">
            <button onClick={() => setPerServing(true)} data-testid="toggle-per-serving"
              className={`px-4 h-10 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200 ${perServing ? "bg-foreground text-background" : "hover:bg-muted"}`}>
              Per serving
            </button>
            <button onClick={() => setPerServing(false)} data-testid="toggle-total"
              className={`px-4 h-10 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200 ${!perServing ? "bg-foreground text-background" : "hover:bg-muted"}`}>
              Total
            </button>
          </div>
          <button onClick={share} data-testid="share-btn"
            className="h-10 px-3 border border-border hover:bg-muted flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
            <Share size={14} /> Share
          </button>
          {!readOnly && (
            <Link to={`/recipe/${id}/remix`} data-testid="remix-btn"
              className="h-10 px-3 border border-border hover:bg-muted flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
              <ArrowsClockwise size={14} /> Remix
            </Link>
          )}
          <Link to={`/recipe/${id}/cook`} data-testid="cook-btn"
            className="h-10 px-3 border border-border hover:bg-muted flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
            <MicrophoneStage size={14} /> Cook
          </Link>
          {!readOnly && (
            <button onClick={addLog} data-testid="add-to-log-btn"
              className="h-10 px-3 border border-border hover:bg-muted flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
              <CalendarPlus size={14} /> Add to log
            </button>
          )}
          <button onClick={() => window.print()} data-testid="print-btn"
            className="h-10 px-3 border border-border hover:bg-muted flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-200">
            <Printer size={14} /> Print / PDF
          </button>
          {!readOnly && (
            <button onClick={remove} data-testid="delete-recipe-btn"
              className="h-10 w-10 border border-border hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center transition-colors duration-200">
              <Trash size={16} />
            </button>
          )}
        </div>
      </div>

      {/* headline stat */}
      <div className="grid grid-cols-1 md:grid-cols-4 border border-border">
        <div className="p-6 md:col-span-2 border-b md:border-b-0 md:border-r border-border bg-primary text-primary-foreground" data-testid="calories-card">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80 flex items-center gap-2"><Fire size={12} weight="fill"/> Energy · {perServing ? "per serving" : "total"}</div>
          <div className="mt-3 font-mono text-6xl md:text-7xl font-medium tracking-tighter">{num(values?.calories, 0)}</div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] opacity-80 mt-1">kilocalories</div>
        </div>
        <DataCell testId="stat-protein" label="Protein" value={values?.protein} unit="g" dv={dv.protein} />
        <DataCell testId="stat-carbs" label="Carbs" value={values?.carbs} unit="g" dv={dv.carbs} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-border border border-border">
        <div className="bg-card p-6" data-testid="macro-donut">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">Macro split · % kcal</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={macroPie} innerRadius={55} outerRadius={90} dataKey="value" stroke="none">
                {macroPie.map((e) => <Cell key={e.name} fill={e.color} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {macroPie.map(m => (
              <div key={m.name} className="flex items-center justify-between font-mono text-xs">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5" style={{ background: m.color }} />
                  <span>{m.name}</span>
                </div>
                <span className="font-medium">{m.pct}%</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card p-6" data-testid="vitamin-radar">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">Vitamin coverage · % DV</div>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="hsl(var(--border))" />
              <PolarAngleAxis dataKey="nutrient" tick={AXIS_TICK} />
              <Radar dataKey="value" stroke="#3B5A5C" fill="#3B5A5C" fillOpacity={0.35} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card p-6" data-testid="mineral-bars">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-4">Minerals · % DV</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={mineralBars} layout="vertical" margin={BAR_MARGIN}>
              <XAxis type="number" tick={AXIS_TICK} />
              <YAxis type="category" dataKey="name" width={80} tick={AXIS_TICK} />
              <Tooltip cursor={TOOLTIP_CURSOR} contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="pct" fill="#7A9A7C" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Full nutrient profile — reusable table + infographic view */}
      <div data-testid="nutrient-profile-section">
        <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Full nutrient profile</h2>
        <NutrientView
          nutrients={values || {}}
          dri={dv}
          defaultTab="standard"
          defaultView="dashboard"
          testIdPrefix="recipe-nutrient-view"
        />
      </div>

      {/* Ingredients table */}
      <div>
        <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Ingredient breakdown</h2>
        <div className="border border-border overflow-x-auto" data-testid="ingredient-table">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Ingredient</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Amount</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Grams</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">kcal</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">P</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">C</th>
                <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">F</th>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Src</th>
              </tr>
            </thead>
            <tbody>
              {recipe.ingredients.map((ing, i) => {
                const kcal = (ing.nutrients.calories || 0) * ing.grams / 100;
                const p = (ing.nutrients.protein || 0) * ing.grams / 100;
                const c = (ing.nutrients.carbs || 0) * ing.grams / 100;
                const f = (ing.nutrients.fat || 0) * ing.grams / 100;
                return (
                  <tr key={`${ing.name}-${i}`} className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors duration-150">
                    <td className="px-4 py-3 font-medium capitalize">{ing.name}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{num(ing.quantity)} {ing.unit}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm text-muted-foreground">{num(ing.grams, 0)}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{num(kcal, 0)}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{num(p)}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{num(c)}</td>
                    <td className="px-4 py-3 text-right font-mono text-sm">{num(f)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border ${ing.source === "db" ? "border-accent/60 text-accent" : "border-primary/60 text-primary"}`}>
                        {ing.source === "db" ? <Database size={10} /> : <Sparkle size={10} />} {ing.source}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {amino && (
        <div data-testid="amino-panel">          <h2 className="font-display font-bold text-2xl tracking-tight mb-3 flex items-center gap-2">
            <Barbell size={22} className="text-primary" /> Protein quality · PDCAAS
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-px bg-border border border-border">
            <div className="bg-primary text-primary-foreground p-6" data-testid="amino-score">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">PDCAAS score</div>
              <div className="mt-2 font-mono text-6xl font-medium tracking-tighter">{amino.score.pdcaas.toFixed(2)}</div>
              <div className="font-mono text-xs uppercase tracking-[0.2em] opacity-80 mt-1">0.00 = incomplete · 1.00 = complete</div>
              <div className="mt-4 font-mono text-xs">
                Limiting AA: <span className="font-bold uppercase">{amino.score.limiting_aa.replace("_", " ")}</span>
              </div>
              <div className="font-mono text-xs opacity-80">Digestibility: {(amino.score.digestibility * 100).toFixed(0)}%</div>
              <div className="font-mono text-xs opacity-80">Protein: {amino.protein_per_serving} g / serving</div>
            </div>
            <div className="bg-card p-6" data-testid="amino-breakdown">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Essential amino acids · vs FAO reference (mg / g protein)</div>
              <div className="space-y-2">
                {Object.entries(amino.score.aa_scores).map(([aa, s]) => {
                  const per = amino.score.per_g_protein_mg[aa];
                  const ref = amino.score.fao_pattern[aa];
                  const pct = Math.min(200, Math.round(s * 100));
                  const passing = s >= 1.0;
                  return (
                    <div key={aa} data-testid={`aa-${aa}`}>
                      <div className="flex items-baseline justify-between">
                        <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">{aa.replace("_", " ")}</span>
                        <span className="font-mono text-xs">
                          <span className="font-medium">{per}</span>
                          <span className="text-muted-foreground"> / {ref}</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-muted mt-1 overflow-hidden">
                        <div className={`h-full ${passing ? "bg-accent" : "bg-primary"} transition-all duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
                      </div>
                      <div className="mt-0.5 font-mono text-[9px] text-muted-foreground">Score {s.toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
              <p className="mt-4 font-mono text-[10px] text-muted-foreground leading-relaxed">
                PDCAAS = min(amino acid score) × true digestibility. Scores &ge; 1.00 indicate a protein complete for adult needs.
                Meat/egg/dairy typically score 1.00; plant proteins can too when properly combined (e.g. grains + legumes).
              </p>
            </div>
          </div>

          {/* Smart Substitutions */}
          <div className="mt-6" data-testid="subs-panel">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
                <ArrowsClockwise size={20} className="text-primary" /> Smart substitutions
              </h3>
              {!subs ? (
                <button onClick={loadSubs} disabled={subsLoading} data-testid="subs-load"
                  className="h-9 px-3 border border-primary text-primary hover:bg-primary hover:text-primary-foreground font-mono text-xs uppercase tracking-[0.15em] disabled:opacity-60 flex items-center gap-1.5 transition-colors duration-200">
                  <Sparkle size={14} /> {subsLoading ? "Analyzing…" : "Suggest swaps"}
                </button>
              ) : (
                <button onClick={loadSubs} disabled={subsLoading}
                  className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground">
                  Refresh
                </button>
              )}
            </div>
            {subs && (subs.suggestions?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
                {subs.suggestions.map((s, i) => (
                  <div key={`${s.kind}-${s.ingredient}-${i}`} className="bg-card p-4" data-testid={`sub-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 border border-border flex items-center justify-center shrink-0 font-display font-black text-lg"
                           style={{ background: s.color + "22", color: s.color }}>
                        {s.ingredient[0].toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                          {s.kind === "add" ? `Add ${s.grams} g` : `Swap ${s.replaces}`}
                        </div>
                        <div className="font-display font-bold text-lg capitalize truncate">{s.ingredient}</div>
                        <p className="text-sm text-muted-foreground mt-1 leading-snug">{s.reason}</p>
                        <div className="mt-2 flex items-baseline gap-3">
                          <div className="font-mono text-xs">
                            <span className="text-muted-foreground">PDCAAS</span> {s.pdcaas_before.toFixed(2)} → <span className="font-bold text-primary">{s.pdcaas_after.toFixed(2)}</span>
                          </div>
                          <div className="font-mono text-xs text-accent font-medium">+{s.delta.toFixed(2)}</div>
                        </div>
                        {!readOnly && (
                          <button onClick={() => applySub(s)} data-testid={`apply-sub-${i}`}
                            className="mt-3 h-8 px-3 bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors duration-200">
                            <ArrowsClockwise size={12} /> Apply & Remix
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-dashed border-border p-4 font-mono text-sm text-muted-foreground">
                No high-impact swaps found — this recipe's protein is already well balanced.
              </div>
            ))}
          </div>
        </div>
      )}

      {combos.length > 0 && (
        <div data-testid="combinations-panel">
          <h2 className="font-display font-bold text-2xl tracking-tight mb-3 flex items-center gap-2">
            <FlowArrow size={22} className="text-primary" /> Ingredient interactions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
            {combos.map((c, i) => {
              const isWarn = c.type === "warning";
              return (
                <div key={`${c.title}-${i}`} className="bg-card p-4" data-testid={`combo-${c.type}-${i}`}>
                  <div className="flex items-start gap-2">
                    {isWarn ? (
                      <WarningIcon size={18} weight="fill" className="text-primary shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle size={18} weight="fill" className="text-accent shrink-0 mt-0.5" />
                    )}
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        {isWarn ? "Adverse combination" : "Synergy"}
                      </div>
                      <div className="font-display font-bold text-lg mt-0.5">{c.title}</div>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{c.effect}</p>
                      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                        Source: {c.source}
                      </div>
                      {c.matched?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {c.matched.map((m, k) => (
                            <span key={`${m}-${k}`} className="font-mono text-[10px] px-1.5 py-0.5 border border-border">{m}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="font-mono text-[11px] text-muted-foreground max-w-3xl leading-relaxed">
        Data derived from USDA FoodData Central, FAO INFOODS, and CIQUAL databases. AI-estimated values (marked <span className="text-primary">ai</span>) are research-informed approximations and should not replace professional dietary advice.
      </p>
    </div>
  );
}
